import {
    ActiveRunInfo,
    AssistantEventEnvelope,
    RunStatusEventData,
    ThreadItem,
    ToolProgressEntry,
    BudgetStatus,
    UsageEventData
} from '../api/types';

export type ActiveRunPhase = 'running' | 'awaitingApproval';

export interface ThreadRunState {
    items: ThreadItem[];
    activeRun: {runId: string; phase: ActiveRunPhase} | null;
    pendingApprovalItemId: string | null;
    statusMessage: string | null;
    usage: UsageEventData | null;
    /** The allowances after the last run that finished in this session. */
    budget: BudgetStatus | null;
    error: string | null;
    lastEventAt: number;
}

export const initialThreadRunState: ThreadRunState = {
    items: [],
    activeRun: null,
    pendingApprovalItemId: null,
    statusMessage: null,
    usage: null,
    budget: null,
    error: null,
    lastEventAt: 0
};

export type ThreadRunAction =
    | {kind: 'event'; envelope: AssistantEventEnvelope; now: number}
    | {
          kind: 'replaceFromServer';
          items: ThreadItem[];
          activeRun: ActiveRunInfo | null | undefined;
          now: number;
      }
    | {kind: 'optimisticUserMessage'; item: ThreadItem}
    | {kind: 'rollbackOptimistic'; itemId: string}
    | {kind: 'reset'};

const OPTIMISTIC_PREFIX = 'optimistic-';

export const isOptimisticId = (id: string) => id.startsWith(OPTIMISTIC_PREFIX);

export const createOptimisticId = () =>
    `${OPTIMISTIC_PREFIX}${Math.random().toString(36).slice(2)}`;

/**
 * Upserts an item by id. A freshly arrived user message replaces a pending
 * optimistic one (the swap pattern from the reference implementation).
 */
const upsertItem = (
    items: ThreadItem[],
    incoming: ThreadItem
): ThreadItem[] => {
    const existingIndex = items.findIndex(item => item.id === incoming.id);
    if (existingIndex >= 0) {
        const next = [...items];
        next[existingIndex] = incoming;
        return next;
    }

    if (incoming.kind === 'userMessage') {
        const optimisticIndex = items.findIndex(
            item => item.kind === 'userMessage' && isOptimisticId(item.id)
        );
        if (optimisticIndex >= 0) {
            const next = [...items];
            next[optimisticIndex] = incoming;
            return next;
        }
    }

    return [...items, incoming];
};

const appendText = (
    items: ThreadItem[],
    itemId: string,
    delta: string,
    field: 'assistantMessage' | 'reasoning'
): ThreadItem[] => {
    return items.map(item => {
        if (item.id !== itemId || item.kind !== field) {
            return item;
        }

        return {...item, text: item.text + delta};
    });
};

const mapRunStatus = (
    status: RunStatusEventData['status']
): ActiveRunPhase | null => {
    switch (status) {
        case 'pending':
        case 'in_progress':
            return 'running';
        case 'paused_for_approval':
            return 'awaitingApproval';
        default:
            return null;
    }
};

const applyEvent = (
    state: ThreadRunState,
    envelope: AssistantEventEnvelope,
    now: number
): ThreadRunState => {
    const base = {...state, lastEventAt: now};
    const data = envelope.data as Record<string, unknown> | null;

    switch (envelope.type) {
        case 'run.created': {
            const runId = (data?.runId as string) ?? envelope.runId ?? '';
            return {
                ...base,
                activeRun: {runId, phase: 'running'},
                pendingApprovalItemId: null,
                statusMessage: null,
                error: null
            };
        }

        case 'run.status': {
            const statusData = envelope.data as RunStatusEventData;
            const phase = mapRunStatus(statusData.status);
            return {
                ...base,
                activeRun: phase ? {runId: statusData.runId, phase} : null,
                pendingApprovalItemId:
                    phase === 'awaitingApproval'
                        ? (statusData.pendingApprovalItemId ??
                          state.pendingApprovalItemId)
                        : null,
                error:
                    statusData.status === 'failed'
                        ? (statusData.errorMessage ?? 'The run failed.')
                        : state.error
            };
        }

        case 'item.added':
        case 'item.completed': {
            const item = data?.item as ThreadItem | undefined;
            if (!item) {
                return base;
            }

            return {...base, items: upsertItem(state.items, item)};
        }

        case 'text.delta': {
            const itemId = data?.itemId as string;
            const delta = (data?.delta as string) ?? '';
            return {
                ...base,
                items: appendText(
                    state.items,
                    itemId,
                    delta,
                    'assistantMessage'
                )
            };
        }

        case 'reasoning.delta': {
            const itemId = data?.itemId as string;
            const delta = (data?.delta as string) ?? '';
            return {
                ...base,
                items: appendText(state.items, itemId, delta, 'reasoning')
            };
        }

        case 'tool.progress': {
            const itemId = data?.itemId as string;
            const message = (data?.message as string) ?? '';
            // Structured steps (code runtime) carry an entry; plain tools only a message.
            const entry = data?.entry as ToolProgressEntry | undefined;
            return {
                ...base,
                statusMessage: message,
                items: state.items.map(item => {
                    if (item.id !== itemId || item.kind !== 'toolCall') {
                        return item;
                    }

                    return entry
                        ? {
                              ...item,
                              progressEntries: [
                                  ...(item.progressEntries ?? []),
                                  entry
                              ]
                          }
                        : {
                              ...item,
                              progressMessages: [
                                  ...(item.progressMessages ?? []),
                                  message
                              ]
                          };
                })
            };
        }

        case 'approval.required': {
            const item = data?.item as ThreadItem | undefined;
            const itemId = (data?.itemId as string) ?? item?.id ?? null;
            return {
                ...base,
                items: item ? upsertItem(state.items, item) : state.items,
                activeRun: state.activeRun
                    ? {...state.activeRun, phase: 'awaitingApproval'}
                    : state.activeRun,
                pendingApprovalItemId: itemId
            };
        }

        case 'usage': {
            const usage = envelope.data as UsageEventData;
            return {...base, usage, budget: usage.budget ?? state.budget};
        }

        case 'status': {
            return {...base, statusMessage: (data?.message as string) ?? null};
        }

        case 'error': {
            return {
                ...base,
                error: (data?.message as string) ?? 'Something went wrong.'
            };
        }

        case 'done': {
            // Terminal: run.status already set the final run/pending state for pauses;
            // if the run is still marked running, clear it.
            return {
                ...base,
                activeRun:
                    state.activeRun?.phase === 'running'
                        ? null
                        : state.activeRun,
                statusMessage: null
            };
        }

        default:
            return base;
    }
};

export const threadRunReducer = (
    state: ThreadRunState,
    action: ThreadRunAction
): ThreadRunState => {
    switch (action.kind) {
        case 'event':
            return applyEvent(state, action.envelope, action.now);

        case 'replaceFromServer': {
            const activeRun = action.activeRun;
            const phase = activeRun ? mapRunStatus(activeRun.status) : null;
            return {
                ...state,
                items: action.items,
                activeRun:
                    activeRun && phase ? {runId: activeRun.runId, phase} : null,
                pendingApprovalItemId:
                    phase === 'awaitingApproval'
                        ? (activeRun?.pendingApprovalItemId ?? null)
                        : null,
                lastEventAt: action.now
            };
        }

        case 'optimisticUserMessage':
            return {
                ...state,
                items: [...state.items, action.item],
                error: null
            };

        case 'rollbackOptimistic':
            return {
                ...state,
                items: state.items.filter(item => item.id !== action.itemId)
            };

        case 'reset':
            return initialThreadRunState;

        default:
            return state;
    }
};
